import { css } from '@compiled/react';
import React, { useCallback, useContext, useEffect } from 'react';
import { useLoaderData, useLocation, useNavigate } from 'react-router-dom';
import {
  ContentType,
  Heading,
  InformationCardState,
  LearningControlPanel,
  useDeviceSize,
} from '@atlassian/learning-components';
import { token } from '@atlaskit/tokens';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';

import { useAtlassianUser } from 'src/hooks/useAtlassianUser';
import { HelmetContext } from 'src/components/Helmet';
import { sendScreenEvent } from 'src/utils/analytics';
import { getSlug } from 'src/utils/slug';
import { getCourseUrl, withPrefix } from 'src/utils/getUrl';
import { getAssetUrl } from 'src/services/http/user';
import { CourseListWrapper, LearningPathInformationCard } from 'src/pages/LearningPathPage/children';
import { ContentHeader } from 'src/components/ContentHeader';
import { getNextItemSlug } from 'src/pages/utils/navigation';
import { LearningProgressStatus } from 'src/services/http/progress';
import { LearningPathLoaderProps } from 'src/pages/LearningPathPage/loader';
import { useProgressState, useCourseProgress } from 'src/store/progress/progressStore';
import { useSaveRecentlyViewed } from 'src/hooks/useSaveRecentlyViewed';
import { useReferrer } from 'src/hooks/useReferrer';
import { useRedirectObject } from 'src/hooks/useRedirectObject';
import { useSaveContent } from 'src/hooks/useSaveContent';
import { getOptions } from 'src/pages/LessonPage/children/utils/getOptions';
import { DISCLAIMER_TEXT, FIELD_ID } from 'src/pages/LearningPathPage/constants';
import { useStartContent } from 'src/hooks/useStartContent';
import { VersioningModalWrapper } from 'src/components/VersioningModalWrapper';

export const LearningPathPage: React.FC = () => {
  const { learningPath, notifications } = useLoaderData() as LearningPathLoaderProps;
  const { user, isLoading, hasError } = useAtlassianUser();
  const helmet = useContext(HelmetContext);
  const navigate = useNavigate();
  const location = useLocation();
  const deviceWidth = useDeviceSize();
  const [, { fetchProgressForCourses, fetchProgressForLessons }] = useProgressState();
  const [{ progressForCourses }] = useCourseProgress();
  const { referrerPath, isInitialized } = useReferrer();
  const { isContentSaved, saveContent } = useSaveContent({
    contentId: learningPath?.sys.id,
    contentType: ContentType.LEARNING_PATH,
    learningPathId: learningPath?.sys.id,
    user,
  });

  const redirectSlug = getSlug(learningPath);
  const coursesCount = learningPath?.coursesCollection?.items?.length || 0;

  useRedirectObject({ ':pathId': redirectSlug });

  helmet.setHelmet({
    title: `${learningPath.title} | Learning - Atlassian Community`,
    seoTags: learningPath.seo,
  });

  useEffect(() => {
    if (!isLoading && !hasError && learningPath && isInitialized) {
      sendScreenEvent(
        'learningPathScreen',
        {
          learningPathSlug: redirectSlug,
          learningReferrerPath: referrerPath,
        },
        user?.account_id,
      );
    }
  }, [user, isLoading, hasError, learningPath, redirectSlug, referrerPath, isInitialized]);

  useEffect(() => {
    if (user && learningPath) {
      fetchProgressForCourses(learningPath, user);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [learningPath, user]);

  useEffect(() => {
    if (user && learningPath) {
      const allLessonIds = learningPath.coursesCollection.items.flatMap((course) =>
        course.lessonsCollection.items.map((lesson) => lesson.sys.id),
      );

      fetchProgressForLessons(allLessonIds, user);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [learningPath, user]);

  useSaveRecentlyViewed(learningPath?.sys.id, ContentType.LEARNING_PATH);

  const handleHrefLink = useCallback((): string => {
    const courses = learningPath?.coursesCollection?.items;
    const locationPrefix = location.pathname.replace(/\/$/, '');

    if (!courses?.length) {
      return '';
    }

    const nextCourseSlug = getNextItemSlug({
      items: courses,
      progress: {
        overallStatus: progressForCourses?.overallStatus || LearningProgressStatus.NOT_STARTED,
        courses: progressForCourses?.courses || [],
      },
      itemIdField: FIELD_ID,
      contentType: ContentType.LEARNING_PATH,
    });

    return withPrefix(`${locationPrefix}${getCourseUrl(nextCourseSlug)}`);
  }, [learningPath, location.pathname, progressForCourses]);

  const getCourseState = (): InformationCardState => {
    if (isLoading) {
      return InformationCardState.LOADING;
    }
    if (!user) {
      return InformationCardState.DEFAULT;
    }

    return InformationCardState.DEFAULT;
  };

  const navigateToCourse = useCallback(
    (courseSlug: string) => navigate(withPrefix(getCourseUrl(courseSlug), location.pathname.replace(/\/$/, ''))),
    [navigate, location],
  );

  const startLearningPath = useStartContent({
    items: learningPath?.coursesCollection?.items,
    progress: progressForCourses,
    contentSlug: redirectSlug,
    contentType: ContentType.LEARNING_PATH,
    userId: user?.account_id,
    navigateToItem: navigateToCourse,
  });

  return (
    <>
      <VersioningModalWrapper notifications={notifications} resourceTitle={learningPath.title} />
      <main css={learningPathMainContainerStyles} data-testid="learning-path-page">
        <div
          css={[learningPathBannerStyles]}
          style={
            {
              '--desktop-banner': `url(${getAssetUrl('/images/contentBanners/learningPathBanner.svg')})`,
              '--mobile-banner': `url(${getAssetUrl('/images/contentBanners/mobileLearningPathBanner.svg')})`,
            } as Record<string, string>
          }
          aria-label="Learning Path Banner"
        />
        <div css={learningPathContentContainerStyles} data-testid="main-content-container">
          <div css={learningPathFirstColumnStyles}>
            <ContentHeader content={learningPath} contentType={ContentType.LEARNING_PATH} />
          </div>
          <div css={learningPathAbsoluteInformationCardStyles}>
            <LearningPathInformationCard urlToShare={handleHrefLink()} />
          </div>
        </div>
        <div css={learningPathFullWidthGreyBackgroundStyles}>
          <div css={learningPathCenteredContentStyles}>
            <div
              css={[
                learningPathCoursesContentStyles,
                css({ minHeight: `calc(250px + ${(learningPath?.video && '100px') || '0px'})` }),
              ]}
              data-testid="course-list-section"
            >
              <Heading level={4} color={token('color.text')}>
                Courses list
              </Heading>
              <div css={[learningPathWrapperStyles]}>
                <CourseListWrapper />
                <div css={[learningPathSpacerStyles, css({ height: `calc(max(150px, ${coursesCount} * 50px))` })]} />
              </div>
            </div>
          </div>
        </div>
        <div css={learningPathDisclaimerContainerStyles}>
          <div css={learningPathFirstColumnStyles}>
            <div css={learningPathDisclaimerSectionStyles}>
              {learningPath?.disclaimer ? (
                documentToReactComponents(
                  learningPath.disclaimer.json,
                  getOptions(learningPath.disclaimer, deviceWidth),
                )
              ) : (
                <div css={learningPathDisclaimerTextStyles}>
                  {DISCLAIMER_TEXT}
                  <a
                    href="https://www.atlassian.com/licensing/purchase-licensing#pricing-discounts"
                    css={learningPathDisclaimerLinkStyles}
                  >
                    visit our FAQs.
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
        <div css={learningPathStickyControlPanelStyles}>
          <LearningControlPanel
            isSaved={isContentSaved}
            onSave={saveContent}
            urlToShare={handleHrefLink()}
            state={getCourseState()}
            contentType={ContentType.LEARNING_PATH}
            onClick={startLearningPath}
          />
        </div>
      </main>
    </>
  );
};

const learningPathMainContainerStyles = css({
  width: '100%',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  marginTop: '136px !important',
  position: 'relative',

  '@media (max-width: 1279px)': {
    marginTop: '0 !important',
  },

  '@media (max-width: 768px)': {
    marginTop: '0 !important',
  },
});

const learningPathBannerStyles = css({
  minHeight: '119px',
  width: '100%',
  objectFit: 'cover',
  backgroundImage: 'var(--desktop-banner)',
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  backgroundRepeat: 'no-repeat',

  '@media (max-width: 965px)': {
    backgroundImage: 'var(--mobile-banner)',
    minHeight: '60px',
  },
});

const learningPathContentContainerStyles = css({
  display: 'flex',
  gap: token('space.300'),
  maxWidth: '1200px',
  margin: '0 auto',
  paddingTop: '45px',
  width: '100%',
  position: 'relative',
  zIndex: 1,
  paddingLeft: token('space.300'),
  paddingRight: token('space.300'),

  '@media (max-width: 1200px)': {
    flexDirection: 'column',
    gap: token('space.300'),
  },
});

const learningPathFirstColumnStyles = css({
  display: 'flex',
  width: '753px',
  flexDirection: 'column',

  '@media (max-width: 965px)': {
    width: '100%',
    order: 1,
  },
});

const learningPathAbsoluteInformationCardStyles = css({
  position: 'absolute',
  right: '24px',
  width: '395px',
  maxWidth: '395px',
  backgroundColor: token('color.text.inverse'),
  borderRadius: '8px',

  '@media (max-width: 965px)': {
    position: 'static',
    width: '100%',
    maxWidth: 'none',
    order: 2,
    borderRadius: '8px',
    paddingBottom: token('space.300'),
  },
});

const learningPathDisclaimerSectionStyles = css({
  height: '99px',
  width: '100%',
  marginTop: '21px',

  '@media (max-width: 965px)': {
    height: '66px',
  },

  '@media (max-width: 768px)': {
    height: '91px !important',
  },
});

const learningPathDisclaimerTextStyles = css({
  color: '#000',
  fontFamily: 'Inter, sans-serif',
  fontSize: '16px',
  fontStyle: 'normal',
  fontWeight: 400,
  lineHeight: '24px',
});

const learningPathDisclaimerLinkStyles = css({
  color: token('color.text.accent.blue'),
  fontFamily: 'Inter, sans-serif',
  fontSize: '16px',
  fontStyle: 'normal',
  fontWeight: 400,
  lineHeight: '24px',
  textDecorationLine: 'underline',
});

const learningPathFullWidthGreyBackgroundStyles = css({
  width: '100%',
  background: 'linear-gradient(90deg, #F8F8F8 0%, #F8F8F8 49.58%, rgba(248, 248, 248, 0.25) 100%)',
});

const learningPathCenteredContentStyles = css({
  maxWidth: '1200px',
  margin: '0 auto',
  padding: `${token('space.300')} 0`,
  display: 'flex',
  justifyContent: 'flex-start',

  '@media (max-width: 1200px)': {
    paddingLeft: token('space.300'),
    paddingRight: token('space.300'),
    order: 3,
  },

  '@media (max-width: 768px)': {
    paddingLeft: token('space.200'),
    paddingRight: token('space.200'),
  },
});

const learningPathCoursesContentStyles = css({
  display: 'flex',
  width: '753px',
  flexDirection: 'column',
  gap: token('space.600'),
  minHeight: '250px',

  '@media (max-width: 965px)': {
    maxHeight: 'unset',
  },

  '@media (min-width: 921px) and (max-width: 1200px)': {
    width: '100%',
  },
});

const learningPathWrapperStyles = css({
  '@media (min-width: 921px) and (max-width: 1200px)': {
    display: 'flex',
    flexDirection: 'row',
    width: '100%',
  },
});

const learningPathSpacerStyles = css({
  display: 'none',

  '@media (min-width: 965px) and (max-width: 1200px)': {
    display: 'flex',
    width: '750px',
    visibility: 'hidden',
  },
});

const learningPathDisclaimerContainerStyles = css({
  display: 'flex',
  gap: token('space.300'),
  maxWidth: '1200px',
  margin: '0 auto',
  paddingTop: 0,
  width: '100%',
  position: 'relative',
  zIndex: 1,

  '@media (max-width: 965px)': {
    order: 5,
    flexDirection: 'column',
    gap: token('space.300'),
  },

  '@media (max-width: 1200px)': {
    flexDirection: 'column',
    gap: token('space.300'),
    paddingLeft: token('space.300'),
    paddingRight: token('space.300'),
  },

  '@media (max-width: 768px)': {
    paddingLeft: token('space.200'),
    paddingRight: token('space.200'),
    height: '100%',
  },
});

const learningPathStickyControlPanelStyles = css({
  display: 'none',

  '@media (max-width: 965px)': {
    display: 'block',
    position: 'fixed',
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: token('color.text.inverse'),
    zIndex: 1000,
  },
});
